import React, { useMemo } from 'react';
import { Button, Col, Form, Input, notification, Row, Select, Space, Typography } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createBlocksRequest, getListBlocksRequest } from '../../../../services/onlinestore/blockServiceRequest';
import { getListMiniAppsRequest } from '../../../../services/onlinestore/miniAppsServiceRequest';
import { AppSelect } from '../../../../constants/appSelect';
import { CloseOutlined, ContainerOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Text, Title } = Typography;

const CreateBlock = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const watchDefaultDisplay = Form.useWatch('default_display', form);

  // get data blocks
  const { data: dataBlocks } = useQuery({
    queryKey: ['dataBlocks'],
    queryFn: () => getListBlocksRequest({ page: 1, limit: 1000, status: [1] }),
  });

  // get data miniapps
  const { data: dataMiniApps } = useQuery({
    queryKey: ['dataMiniApps'],
    queryFn: () => getListMiniAppsRequest({ page: 1, limit: 1000, status: [1] }),
  });

  // convert list block options
  const dataBlocksOption = useMemo(() => {
    return dataBlocks?.data
      ?.filter((item) => item?.status === 1)
      ?.map((item) => {
        return { label: item?.title, value: item?.id };
      });
  }, [dataBlocks]);

  // convert list miniapps options
  const dataMiniAppsOption = useMemo(() => {
    return dataMiniApps?.data
      ?.filter((item) => item?.status === 1)
      ?.map((item) => {
        return { label: item?.title, value: item?.id };
      });
  }, [dataMiniApps]);

  // handle submit form
  const { mutate: mutateCreateBlocks, isPending } = useMutation({
    mutationFn: createBlocksRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Thêm mới khối thành công!',
        });
        navigate(-1);
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (data) => {
    mutateCreateBlocks(data);
  };

  return (
    <Form
      name="create-block"
      form={form}
      layout="vertical"
      className="mb-5"
      onFinish={handleSubmitForm}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Thêm khối</Title>
        </Col>

        <Col xs={24}>
          <Form.Item
            name="title"
            label={
              <Text>
                Tên khối <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập tên khối' }]}
          >
            <Input placeholder="Nhập tên khối..." />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="description"
            label={
              <Text>
                Mô tả <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập mô tả khối' }]}
          >
            <Input placeholder="Nhập mô tả khối..." />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="parent_id" label={<Text>Khối cha</Text>}>
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn khối cha"
              options={dataBlocksOption}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="url" label={<Text>Đường link</Text>}>
            <Input placeholder="Nhập đường link" />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="miniapp_id" label={<Text>MiniApp</Text>}>
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn MiniApp"
              options={dataMiniAppsOption}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="default_display"
            label={
              <Text>
                Loại giao diện <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn loại giao diện' }]}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn loại giao diện"
              options={AppSelect.UI_TYPE}
              allowClear
            />
          </Form.Item>
          {watchDefaultDisplay ? (
            <Space direction="vertical">
              <Text>Preview giao diện</Text>

              <img
                src={`/images/preview/preview_${watchDefaultDisplay}.png`}
                alt=""
                className="w-full h-auto object-contain"
              />
            </Space>
          ) : (
            ''
          )}
        </Col>

        <Col xs={12}>
          <Form.Item
            name="display_place"
            label={
              <Text>
                Vị trí trang hiển thị <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn vị trí trang hiển thị' }]}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              mode="multiple"
              placeholder="Chọn vị trí trang hiển thị"
              options={AppSelect.DISPLAY_PLACE}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="order_place"
            label={
              <Text>
                Thứ tự <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập thứ tự' }]}
          >
            <Input placeholder="Nhập thứ tự..." />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="status"
            label={
              <Text>
                Trạng thái <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default CreateBlock;
